import React, { useState, useEffect } from 'react';
import { api } from '@/App';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, UserCog, Shield, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ConfirmModal from '@/components/ConfirmModal';
import { searchWithoutAccents } from '@/utils/helpers';

const Usuarios = () => {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState({ isOpen: false, id: null });
  const [editingUsuario, setEditingUsuario] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    email: '',
    senha: '',
    role: 'user'
  });

  useEffect(() => {
    loadUsuarios();
  }, []);

  const loadUsuarios = async () => {
    try {
      const response = await api.get('/usuarios');
      setUsuarios(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Acesso negado. Apenas administradores.');
      } else {
        toast.error('Erro ao carregar usuários');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUsuario) {
        await api.put(`/usuarios/${editingUsuario.id}`, formData);
        toast.success('Usuário atualizado com sucesso!');
      } else {
        await api.post('/usuarios', formData);
        toast.success('Usuário criado com sucesso!');
      }
      setDialogOpen(false);
      resetForm();
      loadUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar usuário');
    }
  };

  const handleEdit = (usuario) => {
    setEditingUsuario(usuario);
    setFormData({
      nome: usuario.nome,
      email: usuario.email,
      senha: '',
      role: usuario.role
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/usuarios/${id}`);
      toast.success('Usuário inativado com sucesso!');
      loadUsuarios();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao inativar usuário');
    }
  };

  const resetForm = () => {
    setEditingUsuario(null);
    setFormData({
      nome: '',
      email: '',
      senha: '',
      role: 'user'
    });
  };

  const filteredUsuarios = usuarios.filter(u =>
    !searchTerm ||
    searchWithoutAccents(u.nome, searchTerm) ||
    searchWithoutAccents(u.email, searchTerm)
  );

  return (
    <div className="space-y-6 animate-slide-up" data-testid="usuarios-page">
      <ConfirmModal
        isOpen={confirmDelete.isOpen}
        onClose={() => setConfirmDelete({ isOpen: false, id: null })}
        onConfirm={() => handleDelete(confirmDelete.id)}
        title="Inativar Usuário"
        message="Deseja realmente inativar este usuário? Ele não poderá mais acessar o sistema."
        confirmText="Sim, Inativar"
        cancelText="Cancelar"
      />

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 mb-2 flex items-center">
            <UserCog className="w-8 h-8 mr-3 text-red-600" />
            Gerenciamento de Usuários
          </h1>
          <p className="text-slate-600">Controle de acesso e permissões do sistema</p>
        </div>
        <Button 
          data-testid="add-usuario-button" 
          onClick={() => { resetForm(); setDialogOpen(true); }} 
          className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Novo Usuário
        </Button>
      </div>

      {/* Modal */}
      {dialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setDialogOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto m-4" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-slate-900">{editingUsuario ? 'Editar Usuário' : 'Novo Usuário'}</h2>
              <button onClick={() => setDialogOpen(false)} className="p-2 hover:bg-slate-100 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <Label>Nome Completo</Label>
                <Input
                  data-testid="usuario-nome-input"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label>Email</Label>
                <Input
                  data-testid="usuario-email-input"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div>
                <Label>Senha {editingUsuario && '(deixe em branco para manter a atual)'}</Label>
                <Input
                  data-testid="usuario-senha-input"
                  type="password"
                  value={formData.senha}
                  onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                  required={!editingUsuario}
                  placeholder={editingUsuario ? 'Deixe vazio para não alterar' : ''}
                />
              </div>

              <div>
                <Label>Nível de Acesso</Label>
                <select
                  data-testid="usuario-role-select"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="input-field"
                  required
                >
                  <option value="user">Usuário</option>
                  <option value="admin">Administrador</option>
                </select>
                <p className="text-xs text-slate-500 mt-1">
                  Administradores têm acesso completo ao sistema
                </p>
              </div>

              <div className="flex justify-end space-x-2 pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button data-testid="save-usuario-button" type="submit" className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700">
                  Salvar
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            data-testid="search-usuarios-input"
            placeholder="Buscar por nome ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Email</th>
                  <th>Nível de Acesso</th>
                  <th>Último Login</th>
                  <th>Status</th>
                  <th className="text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsuarios.map((usuario) => (
                  <tr key={usuario.id} data-testid={`usuario-row-${usuario.id}`}>
                    <td className="font-semibold">{usuario.nome}</td>
                    <td>{usuario.email}</td>
                    <td>
                      {usuario.role === 'admin' ? (
                        <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold flex items-center w-fit">
                          <Shield className="w-3 h-3 mr-1" />
                          ADMINISTRADOR
                        </span>
                      ) : (
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">
                          USUÁRIO
                        </span>
                      )}
                    </td>
                    <td className="text-sm text-slate-600">
                      {usuario.ultimo_login_em ? new Date(usuario.ultimo_login_em).toLocaleString('pt-BR') : 'Nunca'}
                    </td>
                    <td>
                      <span className={`status-badge ${usuario.ativo ? 'status-pago' : 'status-cancelado'}`}>
                        {usuario.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="text-right">
                      <div className="flex items-center justify-end space-x-2">
                        <Button
                          data-testid={`edit-usuario-${usuario.id}`}
                          size="sm"
                          variant="outline"
                          onClick={() => handleEdit(usuario)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          data-testid={`delete-usuario-${usuario.id}`}
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => setConfirmDelete({ isOpen: true, id: usuario.id })}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Usuarios;
